import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { DataTable } from '../../../../shared/components/index.js';

interface ChannelMetric { readonly channel: string; readonly sent: number; readonly delivered: number; readonly failed: number; }
interface AnalyticsData {
  readonly deliveryRate: number;
  readonly failureRate: number;
  readonly avgResponseMinutes: number;
  readonly reminderEffectiveness: number;
  readonly channels: readonly ChannelMetric[];
}


export function NotificationsAnalyticsPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const { number } = useBusinessNumber();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<AnalyticsData>('/notifications/analytics', { token })
      .then((r) => { if (alive) setData(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  if (error) return <p role="alert">{error}</p>;
  if (!data) return <p>{t('notif.common.loading')}</p>;
  return (
    <main aria-label="communication-analytics">
      <h1>{t('notif.analytics.title')}</h1>
      <section aria-label="metric-cards" className="card-grid">
        <article className="card"><h2>{t('notif.analytics.deliveryRate')}</h2><p data-testid="m-delivery">{`${number(data.deliveryRate * 100, 1)}%`}</p></article>
        <article className="card"><h2>{t('notif.analytics.failureRate')}</h2><p data-testid="m-failure">{`${number(data.failureRate * 100, 1)}%`}</p></article>
        <article className="card"><h2>{t('notif.analytics.avgResponse')}</h2><p data-testid="m-response">{t('notif.analytics.avgMin', { n: data.avgResponseMinutes })}</p></article>
        <article className="card"><h2>{t('notif.analytics.reminderEffectiveness')}</h2><p data-testid="m-reminder">{`${number(data.reminderEffectiveness * 100, 1)}%`}</p></article>
      </section>
      <h2>{t('notif.analytics.channelUtilization')}</h2>
      <DataTable<ChannelMetric>
        ariaLabel="channel-metrics"
        columns={[
          { key: 'channel', header: t('notif.common.channel'), render: (c) => c.channel },
          { key: 'sent', header: t('notif.common.sent'), render: (c) => c.sent },
          { key: 'delivered', header: t('notif.common.delivered'), render: (c) => c.delivered },
          { key: 'failed', header: t('notif.common.failed'), render: (c) => c.failed },
        ]}
        rows={data.channels}
        rowKey={(c) => c.channel}
      />
    </main>
  );
}
